import React from 'react';
import { Flex, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CustomButton,
  CustomModalWithoutButton,
  CustomParagraph,
} from '@/components';
import { getPickupErrorMessage } from '@/apis/fedexPickup';
import type { FedExPickup } from '@/apis/fedexPickup';
import { useCancelPickup } from '@/hooks/useFedExPickup';

interface CancelPickupModalProps {
  pickup: FedExPickup;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CancelPickupModal: React.FC<CancelPickupModalProps> = ({
  pickup,
  open,
  setOpen,
}) => {
  const { t } = useTranslation('shipments');
  const { cancel, isCancelling } = useCancelPickup(pickup.shipmentId);

  const confirmCancel = async () => {
    try {
      await cancel(pickup.id);
      message.success(t('pickup.cancelled', 'FedEx pickup cancelled'));
    } catch (err) {
      message.error(getPickupErrorMessage(err));
    }
    setOpen(false);
  };

  return (
    <CustomModalWithoutButton
      modalOpen={open}
      setModalOpen={setOpen}
      modalTitle={t('pickup.cancelTitle', 'Cancel FedEx Pickup')}
      modalSize={524}
      modalBorderRadius='16px'
      fontSize='20px'
      lineHeight='28px'
    >
      <Flex vertical gap={20}>
        <CustomParagraph
          text={t(
            'pickup.cancelBody',
            'Confirmation {{code}} will be cancelled with FedEx.',
            { code: pickup.confirmationCode }
          )}
          marginTop='8px'
          marginBottom='4px'
        />
        <Flex gap='16px' justify='end'>
          <CustomButton
            text={t('pickup.keep', 'Keep Pickup')}
            bgColor='transparent'
            color='var(--text-primary)'
            fontSize='14px'
            fontWeight='600'
            boxShadow='none'
            height='36px'
            onClick={() => setOpen(false)}
          />
          <CustomButton
            text={t('pickup.cancelPickup', 'Cancel Pickup')}
            bgColor='var(--danger)'
            color='white'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            boxShadow='none'
            borderRadius='8px'
            disabled={isCancelling}
            onClick={confirmCancel}
          />
        </Flex>
      </Flex>
    </CustomModalWithoutButton>
  );
};

export default CancelPickupModal;
